import React, { useState, useEffect } from 'react'
import { X, Plus, Check, AlertCircle, Package, Zap, Search, Filter } from 'lucide-react'
import { adminApi } from '../services/api'
import toast from 'react-hot-toast'

const FeatureAssignmentModal = ({ tenant, isOpen, onClose, onAssign }) => {
  const [features, setFeatures] = useState({
    assigned: [],
    unassigned: []
  })
  const [loading, setLoading] = useState(false)
  const [selectedFeatures, setSelectedFeatures] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [showOnlyUnassigned, setShowOnlyUnassigned] = useState(false)

  // Category colors and icons
  const categoryConfig = {
    core: { color: 'bg-blue-100 text-blue-800', icon: Package },
    advanced: { color: 'bg-purple-100 text-purple-800', icon: Zap },
    reporting: { color: 'bg-green-100 text-green-800', icon: '📊' },
    integration: { color: 'bg-orange-100 text-orange-800', icon: '🔗' },
    security: { color: 'bg-red-100 text-red-800', icon: '🔒' }
  }

  const planHierarchy = {
    free: 0,
    basic: 1,
    premium: 2,
    enterprise: 3
  }

  const planColors = {
    free: 'bg-gray-100 text-gray-800',
    basic: 'bg-blue-100 text-blue-800',
    premium: 'bg-purple-100 text-purple-800',
    enterprise: 'bg-gold-100 text-gold-800'
  }

  useEffect(() => {
    if (isOpen && tenant) {
      console.log('FeatureAssignmentModal - Tenant:', tenant)
      if (tenant.id) {
        fetchAvailableFeatures()
      } else {
        console.error('Tenant ID is missing:', tenant)
        toast.error('Tenant ID is missing. Please refresh the page.')
      }
    }
  }, [isOpen, tenant])

  const fetchAvailableFeatures = async () => {
    if (!tenant || !tenant.id) {
      console.error('Tenant or tenant.id is missing')
      return
    }

    try {
      setLoading(true)
      const response = await adminApi.getAvailableFeatures(tenant.id)
      setFeatures({
        assigned: response.data.assignedFeatures || [],
        unassigned: response.data.unassignedFeatures || []
      })
    } catch (error) {
      toast.error('Failed to fetch available features')
      console.error('Error fetching features:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFeatureToggle = (featureId) => {
    setSelectedFeatures(prev => 
      prev.includes(featureId) 
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    )
  }

  const handleBulkAssign = async () => {
    if (selectedFeatures.length === 0) {
      toast.error('Please select at least one feature')
      return
    }

    if (!tenant || !tenant.id) {
      toast.error('Tenant information is missing')
      return
    }

    try {
      setLoading(true)
      await adminApi.assignFeatures(tenant.id, selectedFeatures, true)
      toast.success(`${selectedFeatures.length} features assigned successfully`)
      
      // Refresh features
      await fetchAvailableFeatures()
      setSelectedFeatures([])
      
      if (onAssign) {
        onAssign(tenant.id, selectedFeatures)
      }
    } catch (error) {
      if (error.response?.data?.incompatibleFeatures) {
        toast.error(error.response.data.message)
      } else {
        toast.error('Failed to assign features')
      }
      console.error('Error assigning features:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleBulkUnassign = async () => {
    if (selectedFeatures.length === 0) {
      toast.error('Please select at least one feature')
      return
    }

    if (!tenant || !tenant.id) {
      toast.error('Tenant information is missing')
      return
    }

    try {
      setLoading(true)
      await adminApi.assignFeatures(tenant.id, selectedFeatures, false)
      toast.success(`${selectedFeatures.length} features unassigned successfully`)
      
      // Refresh features
      await fetchAvailableFeatures()
      setSelectedFeatures([])
      
      if (onAssign) {
        onAssign(tenant.id, selectedFeatures)
      }
    } catch (error) {
      toast.error('Failed to unassign features')
      console.error('Error unassigning features:', error)
    } finally {
      setLoading(false)
    }
  }

  const isFeatureCompatible = (feature) => {
    if (!tenant || !tenant.subscriptionPlan) return false
    return planHierarchy[feature.requiredPlan] <= planHierarchy[tenant.subscriptionPlan]
  }

  const getFilteredFeatures = () => {
    const allFeatures = showOnlyUnassigned ? features.unassigned : [...features.assigned, ...features.unassigned]
    
    return allFeatures.filter(feature => {
      const matchesSearch = feature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           feature.description?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = categoryFilter === 'all' || feature.category === categoryFilter
      
      return matchesSearch && matchesCategory
    })
  }

  const groupedFeatures = getFilteredFeatures().reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = []
    }
    acc[feature.category].push(feature)
    return acc
  }, {})

  const categories = Object.keys(groupedFeatures).sort()

  if (!isOpen || !tenant || !tenant.id) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Assign Features to {tenant.name || 'Tenant'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Current plan: <span className={`px-2 py-1 rounded-full text-xs font-medium ${planColors[tenant.subscriptionPlan] || planColors.free}`}>
                {tenant.subscriptionPlan?.toUpperCase() || 'FREE'}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search features..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                <option value="core">Core</option>
                <option value="advanced">Advanced</option>
                <option value="reporting">Reporting</option>
                <option value="integration">Integration</option>
                <option value="security">Security</option>
              </select>
            </div>

            {/* Show Only Unassigned */}
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showOnlyUnassigned}
                onChange={(e) => setShowOnlyUnassigned(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Only unassigned</span>
            </label>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading features...</span>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No features found matching your criteria
            </div>
          ) : (
            <div className="space-y-6">
              {categories.map(category => (
                <div key={category} className="bg-gray-50 rounded-lg p-4">
                                     <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                     {typeof categoryConfig[category]?.icon === 'string' ? (
                       <span className="mr-2 text-lg">{categoryConfig[category].icon}</span>
                     ) : (
                       categoryConfig[category]?.icon && 
                       React.createElement(categoryConfig[category].icon, { className: "mr-2", size: 18 })
                     )}
                     {category.charAt(0).toUpperCase() + category.slice(1)}
                     <span className="ml-2 text-sm text-gray-500">
                       ({groupedFeatures[category].length})
                     </span>
                   </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {groupedFeatures[category].map(feature => {
                      const isAssigned = feature.isAssigned
                      const isEnabled = feature.isEnabled
                      const isCompatible = isFeatureCompatible(feature)
                      const isSelected = selectedFeatures.includes(feature.id)
                      
                      return (
                        <div
                          key={feature.id}
                          className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          } ${!isCompatible ? 'opacity-50' : ''}`}
                          onClick={() => isCompatible && handleFeatureToggle(feature.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="font-medium text-gray-900">{feature.name}</h4>
                                {isAssigned && (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {isEnabled ? 'Enabled' : 'Disabled'}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                              
                              <div className="flex items-center mt-2 space-x-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${categoryConfig[feature.category]?.color}`}>
                                  {feature.category}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${planColors[feature.requiredPlan]}`}>
                                  {feature.requiredPlan}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2 ml-3">
                              {!isCompatible && (
                                <AlertCircle className="text-orange-500" size={16} />
                              )}
                              {isSelected && (
                                <Check className="text-blue-600" size={16} />
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedFeatures.length} feature{selectedFeatures.length !== 1 ? 's' : ''} selected
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                Cancel
              </button>
              
              <button
                onClick={handleBulkUnassign}
                disabled={selectedFeatures.length === 0 || loading}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Unassign Selected
              </button>
              
              <button
                onClick={handleBulkAssign}
                disabled={selectedFeatures.length === 0 || loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Assigning...' : 'Assign Selected'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FeatureAssignmentModal 
